import { Component, OnInit } from '@angular/core';
import { ApiService } from '@shared/services/api.service';
import { Router, ActivatedRoute } from '@angular/router';
import { OAuthService } from '@app/shared/services/oauth.service';
import { APIService, NotionAuthentication, NotionLibraryDatabase } from '@app/API.service';
import { Auth, Storage } from 'aws-amplify';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';

export interface PeriodicElement {
  name: string;
  position: string;
  weight: number;
  symbol: string;
}

const ELEMENT_DATA: PeriodicElement[] = [
  {position: 'The Interior Castle', name: 'St. Teresa of Avilla', weight: 13, symbol: '10-17-2021'},
  {position: 'Where Wizards Stay Up Late', name: 'Matthew Lyon, Katie Hafner', weight: 6, symbol: '9-12-2021'},
  {position: 'Politics', name: 'Aristotle and Bejnamin Jowett', weight: 28, symbol: '9-13-2021'},
  {position: 'Get Together', name: 'Bailey Richardson, Kevin Huynh, and Kai ELmer Sotto', weight: 5, symbol: '7-05-2021'},
  {position: 'Creativity, Inc.', name: 'Ed Catmull and AMy Wallace', weight: 83, symbol: '9-04-2021'},
  {position: 'The Jesus Legend', name: 'Paul Rhodes Eddy and Gregory A. Boyd', weight: 42, symbol: '9-03-2021'},
  {position: 'Why Zebras Don\'t Get Ulcers', name: 'Robert M. Sapolsky', weight: 118, symbol: '7-24-2021'},
  {position: 'How to Talk So Kids WIll Listen & Listen So Kids Will Talk', name: 'Adele Faber, Elaine Mazlish', weight: 24, symbol: '7-04-2021'},
  {position: 'Zero to One', name: 'Peter Thiel, Blake Masters', weight: 41, symbol: '6-15-2021'},
  {position: 'The Great Shift', name: 'James L. Kugel', weight: 106, symbol: '6-03-2021'},
];

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {

  public uploadedClippings: boolean = false;
  public hasNotionLibraryDatabase: boolean = false;
  public hasAuthenticatedNotion: boolean = false;
  public notionLibraryDatabase: NotionLibraryDatabase;
  public notionAuthentication: NotionAuthentication;

  private authenticatedUser: any;
  private s3Key: string;

  public displayedColumns: string[] = ['position', 'name', 'weight', 'symbol'];
  public dataSource = ELEMENT_DATA;

  public libraryDatabaseForm = new FormGroup({
    name: new FormControl('', [
      Validators.required
    ]),
    parentPageId: new FormControl('', [
      Validators.required
    ])
  })

  v2Token: string;
  tableId: string;
  fileToUpload: File = null;
  clippings: string | ArrayBuffer;
  clippingsUploaded: boolean = false;
  isLoading: boolean = true;
  wasSubmitted: boolean = false;

  constructor( 
    private oauthService: OAuthService, 
    private api: ApiService, 
    private router: Router, 
    private route: ActivatedRoute,
    private amplifyApi: APIService,
    private location: Location
  ) { }

  ngOnInit() {

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code != null && this.hasAuthenticatedNotion == false) {
      this.notionTokenExchange(code);
      this.location.go('/home');
    }

    this.setCurrentUser();
    this.setNotionAuthentication();
    this.setNotionLibraryDatabase();

    this.isLoading = false;

  }

  setCurrentUser() {
    Auth.currentAuthenticatedUser().then((user) => {
      this.authenticatedUser = user;
    });
  }

  setNotionAuthentication() {
    this.amplifyApi.ListNotionAuthentications().then((notionAuthentication) => {
      if (notionAuthentication.items.length > 0) {
        this.hasAuthenticatedNotion = true;
        this.notionAuthentication = notionAuthentication.items[0];
        console.log(notionAuthentication.items[0]);
      }
    });
  }

  setNotionLibraryDatabase() {
    this.amplifyApi.ListNotionLibraryDatabases().then((notionLibraryDatabase) => {
      if (notionLibraryDatabase.items.length > 0) {
        this.hasNotionLibraryDatabase = true;
        this.notionLibraryDatabase = notionLibraryDatabase.items[0];
      }
    });
  }

  importNotesFree(): Promise<void> {
    this.isLoading = true;
    this.wasSubmitted = true;
    return new Promise(resolve => {
      this.api.importNotesFree(this.v2Token, this.tableId, this.clippings.toString()).subscribe ((response) => {
        this.isLoading = false;
        resolve();
      });
    });
  }

  premium() {
    //this.router.navigate(['/checkout'], { relativeTo: this.route });
    this.router.navigate(['/upload'], { relativeTo: this.route });
  }

  // --------------------------------------------------------------

  authorize() {
    this.oauthService.loginRedirect();
  }

  notionTokenExchange(code: string) {
    this.amplifyApi.NotionTokenExchange(code).then((response) => {
      const data = JSON.parse(response);
      this.createNotionAuthentication(data);
    }).catch((e) => {
      console.log("Error caught...", e);
    });
  }

  createNotionAuthentication(data) {
    this.amplifyApi.CreateNotionAuthentication({
      access_token: data["access_token"],
      bot_id: data["bot_id"],
      owner: data["owner"],
      workspace_icon: data["workspace_icon"],
      workspace_id: data["workspace_id"],
      workspace_name: data["workspace_name"]
    }).then((response) => {
      this.setNotionAuthentication();
    });
  }

  async createLibraryDatabase() {
    // Create database
    const libraryDatabase = JSON.parse(
      await this.amplifyApi.InitializeNotionLibraryDatabase(
        this.notionAuthentication.access_token,
        this.libraryDatabaseForm.value.parentPageId,
        this.libraryDatabaseForm.value.name
      )
    )

    // Store database id
    await this.amplifyApi.CreateNotionLibraryDatabase({database_id: libraryDatabase.id});

    this.setNotionLibraryDatabase();
  }

  handleFileInput(files: FileList) {
    this.clippingsUploaded = false;
    let reader = new FileReader();
    reader.onload = () => {
      this.clippings = reader.result;
    }
    this.fileToUpload = files.item(0);
    reader.readAsText(this.fileToUpload);
    this.uploadClippingsFile().then(() => {
      this.clippingsUploaded = true;
    });
  }

  async uploadClippingsFile() {
    // filename is username/currentTime
    const now = Date.now().toString();
    let fileName = this.authenticatedUser.username + "/" + now;

    // upload to s3 and get key
    const results = await Storage.put(fileName, this.fileToUpload);
    this.s3Key = results.key

    // create record of the clippings file
    await this.amplifyApi.CreateClippings({s3_key: this.s3Key})
  }

  async exportToNotion() {
    let response = await this.amplifyApi.ExportClippingsToNotion(
      this.notionAuthentication.access_token,
      this.s3Key,
      this.notionLibraryDatabase.database_id
    );
    this.uploadedClippings = true;
  }

}